/**
 * AddTeamMemberDialog Component
 * Modal dialog for adding/removing team members from a project
 * 
 * Separate component following modular architecture principles:
 * - Isolates team management logic from ProjectTeamTab display component
 * - Reusable for other project management contexts
 * - Easier to maintain and test independently
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Checkbox,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
} from '@mui/material';
import { Search, Users } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { Project } from '@/types';
import { userService, projectService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';

interface AddTeamMemberDialogProps {
  open: boolean;
  onClose: () => void;
  project: Project;
}

/**
 * Dialog component for managing project team members
 * Allows adding/removing users from a project's team
 */
const AddTeamMemberDialog: React.FC<AddTeamMemberDialogProps> = ({
  open,
  onClose,
  project,
}) => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    new Set(project.teamMembers || [])
  );

  // Fetch all users
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: QUERY_KEYS.users,
    queryFn: () => userService.getUsers(undefined, { pageSize: 100 }),
  });

  const users = usersData || [];

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    
    const query = searchQuery.toLowerCase();
    return users.filter((user) => {
      const displayName = user.displayName?.toLowerCase() || '';
      const firstName = user.firstName?.toLowerCase() || '';
      const lastName = user.lastName?.toLowerCase() || '';
      const email = user.email?.toLowerCase() || '';
      
      return (
        displayName.includes(query) ||
        firstName.includes(query) ||
        lastName.includes(query) ||
        email.includes(query)
      );
    });
  }, [users, searchQuery]);

  // Update project mutation
  const updateProjectMutation = useMutation({
    mutationFn: (teamMemberIds: string[]) =>
      projectService.updateProject(project.id, { teamMemberIds } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.projects, project.id] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
      onClose();
    },
  });

  // Toggle user selection
  const handleToggleUser = (userId: string) => {
    setSelectedUserIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Handle save
  const handleSave = () => {
    const teamMemberIds = Array.from(selectedUserIds);
    updateProjectMutation.mutate(teamMemberIds);
  };

  // Handle close with reset
  const handleClose = () => {
    setSearchQuery('');
    setSelectedUserIds(new Set(project.teamMembers || []));
    onClose();
  };

  // Calculate changes
  const currentTeamIds = new Set(project.teamMembers || []);
  const addedCount = Array.from(selectedUserIds).filter(
    (id) => !currentTeamIds.has(id)
  ).length;
  const removedCount = Array.from(currentTeamIds).filter(
    (id) => !selectedUserIds.has(id)
  ).length;
  const hasChanges = addedCount > 0 || removedCount > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Users size={24} />
          <Typography variant="h6">Manage Team Members</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Add or remove team members from {project.name}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Search Field */}
        <TextField
          fullWidth
          placeholder="Search users by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            ),
          }}
        />

        {/* Changes Summary */}
        {hasChanges && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {addedCount > 0 && (
                <Chip
                  label={`+${addedCount} Added`}
                  color="success"
                  size="small"
                  variant="outlined"
                />
              )}
              {removedCount > 0 && (
                <Chip
                  label={`-${removedCount} Removed`}
                  color="error"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Alert>
        )}

        {/* User List */}
        {isLoadingUsers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'No users found matching your search' : 'No users available'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredUsers.map((user) => {
              const isSelected = selectedUserIds.has(user.id);
              const displayName =
                user.displayName ||
                (user.firstName && user.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user.email);

              return (
                <ListItem
                  key={user.id}
                  dense
                  disablePadding
                  sx={{ mb: 0.5 }}
                >
                  <ListItemButton
                    onClick={() => handleToggleUser(user.id)}
                    sx={{
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: isSelected ? 'primary.main' : 'grey.400',
                        }}
                      >
                        {displayName.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={displayName}
                      secondary={user.email}
                      primaryTypographyProps={{
                        fontWeight: isSelected ? 500 : 400,
                      }}
                    />
                    {user.role && (
                      <Chip
                        label={user.role}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}

        {/* Selected Count */}
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Selected: {selectedUserIds.size} member{selectedUserIds.size !== 1 ? 's' : ''}
          </Typography>
          {hasChanges && (
            <Typography variant="caption" color="primary">
              Unsaved changes
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges || updateProjectMutation.isPending}
          startIcon={
            updateProjectMutation.isPending ? (
              <CircularProgress size={16} />
            ) : undefined
          }
        >
          {updateProjectMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTeamMemberDialog;
